/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";
import { setTitle } from "@lemmonhq/orders";
import type { Event } from "@lemmonhq/types";
import { useLocalization, ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useAuth } from "@app/vue/providers/index";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { EventForm } from "./EventsManagement/EventForm";

dayjs.extend(utc);
dayjs.extend(timezone);
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { ErrorDisplayer } from "@app/vue/components/ErrorDisplayer";

// Grid styles removed - using ResponsiveTable

export const EventsManagement = defineComponent({
  name: "EventsManagement",
  setup() {
    const { t } = useLocalization();
    const auth = useAuth();

    const restaurantTz = computed(
      () => auth.user.value?.restaurant?.timezone || "UTC",
    );

    const events = ref<Event[]>([]);
    const loading = ref(true);
    const error = ref("");
    const showForm = ref(false);
    const editingEvent = ref<Event | null>(null);
    const deleteConfirmId = ref<string | null>(null);
    const saveError = ref("");
    const deleteError = ref("");

    // Stored times are UTC; render them in the restaurant timezone so managers
    // see the times they entered regardless of their browser timezone.
    const formatDate = (dateString: string) =>
      dayjs(dateString).tz(restaurantTz.value).format("ddd, MMM D, YYYY");

    const formatTime = (dateString: string) =>
      dayjs(dateString).tz(restaurantTz.value).format("hh:mm A");

    const getEventScheduleInfo = (event: Event) => {
      const schedule = event.schedule;
      if (!schedule) {
        return {
          isRecurring: false,
          label: t("events.card.one_time_event"),
          date: event.date || "",
          time: event.time || "",
        };
      }
      if (schedule.type === "one_off") {
        return {
          isRecurring: false,
          label: t("events.card.one_time_event"),
          date: formatDate(schedule.start_at),
          time: formatTime(schedule.start_at),
        };
      } else {
        return {
          isRecurring: true,
          label: t("events.card.recurring_event"),
          date: formatDate(schedule.start_at),
          time: formatTime(schedule.start_at),
          rrule: schedule.rrule,
        };
      }
    };

    const getTicketInfo = (event: Event) => {
      const tickets = event.ticket_types || [];
      if (tickets.length === 0) {
        return { hasTickets: false, count: 0, minPrice: 0 };
      }
      const minPrice = Math.min(...tickets.map((t) => t.price));
      return { hasTickets: true, count: tickets.length, minPrice };
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "event",
        label: t("events.management.column_event"),
        icon: "event_note",
        mobilePriority: "primary",
        render: (row: Event) => (
          <div class="flex items-center gap-3">
            {row.image ? (
              <img
                src={row.image}
                class="w-12 h-12 rounded-lg object-cover bg-base-300 flex-shrink-0"
              />
            ) : (
              <div class="w-12 h-12 rounded-lg bg-base-200 flex items-center justify-center text-base-content/30 flex-shrink-0">
                <span class="material-symbols-outlined text-lg">event</span>
              </div>
            )}
            <div class="flex flex-col min-w-0">
              <span class="font-medium text-accent truncate">{row.title}</span>
              {row.subtitle && (
                <span class="text-xs text-base-content/60 truncate">
                  {row.subtitle}
                </span>
              )}
              {row.tags && row.tags.length > 0 && (
                <div class="flex flex-wrap gap-1 mt-1">
                  {row.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      class="px-1.5 py-0.5 text-[10px] font-medium bg-base-200 text-base-content/75 rounded-full border border-base-300/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: "schedule",
        label: t("events.management.column_schedule"),
        icon: "calendar_today",
        mobilePriority: "secondary",
        render: (row: Event) => {
          const info = getEventScheduleInfo(row);
          return (
            <div class="flex flex-col gap-1">
              <span class="font-medium text-accent text-sm">{info.date}</span>
              <div class="flex items-center gap-1.5">
                <span class="text-base-content/60 text-xs">{info.time}</span>
                <span
                  class={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                    info.isRecurring
                      ? "bg-purple-500/10 text-purple-400"
                      : "bg-blue-500/10 text-blue-400"
                  }`}
                >
                  {info.isRecurring
                    ? t("events.card.recurring_badge")
                    : t("events.card.one_time_badge")}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: "tickets",
        label: t("events.management.column_tickets"),
        icon: "confirmation_number",
        mobilePriority: "secondary",
        render: (row: Event) => {
          const info = getTicketInfo(row);
          if (info.hasTickets) {
            return (
              <span class="text-sm text-base-content/70">
                {info.count} {t("events.card.types_suffix")} • From $
                {info.minPrice}
              </span>
            );
          } else if (row.price_from) {
            return (
              <span class="text-sm text-base-content/70">
                From ${row.price_from}
              </span>
            );
          } else {
            return (
              <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs font-semibold rounded-full">
                <span class="material-symbols-outlined text-xs">
                  check_circle
                </span>
                {t("events.card.free_event")}
              </span>
            );
          }
        },
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "actions",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (row: Event) => (
          <div
            class="flex items-center justify-end gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              class="btn btn-sm bg-primary hover:bg-primary-focus border-0 text-primary-content gap-1.5"
              onClick={() => handleEditEvent(row)}
            >
              <span class="material-symbols-outlined text-base">edit</span>
              <span class="hidden sm:inline">{t("events.card.edit")}</span>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-square bg-error/10 hover:bg-error/20 border border-error/20 text-error"
              onClick={() => handleDeleteEvent(row.id)}
            >
              <span class="material-symbols-outlined text-base">delete</span>
            </button>
          </div>
        ),
      },
    ];

    const loadEvents = async () => {
      try {
        loading.value = true;
        error.value = "";
        const data = await api.getEvents();
        events.value = data as Event[];
      } catch (err) {
        console.error("Failed to load events:", err);
        error.value =
          err instanceof Error ? err.message : "Failed to load events";
        events.value = [];
      } finally {
        loading.value = false;
      }
    };

    const sortedEvents = computed(() => {
      return [...events.value].sort((a, b) => {
        // Sort by schedule type and date
        if (!a.schedule || !b.schedule) return 0;
        const aDate =
          a.schedule.type === "one_off"
            ? new Date(a.schedule.start_at)
            : new Date(a.schedule.start_at);
        const bDate =
          b.schedule?.type === "one_off"
            ? new Date(b.schedule.start_at)
            : new Date(b.schedule.start_at);
        return aDate.getTime() - bDate.getTime();
      });
    });

    const handleAddEvent = () => {
      editingEvent.value = null;
      showForm.value = true;
    };

    const handleEditEvent = (event: Event) => {
      editingEvent.value = event;
      showForm.value = true;
    };

    const handleSaveEvent = async (event: Event) => {
      try {
        saveError.value = "";
        // Extract date and time from schedule if they're not set
        let eventData = { ...event };

        if (event.schedule && (!event.date || !event.time)) {
          const startAt =
            event.schedule.type === "one_off"
              ? event.schedule.start_at
              : event.schedule.start_at;

          if (startAt) {
            // start_at is stored in UTC; derive the legacy date/time display
            // fields in the restaurant timezone.
            const inTz = dayjs(startAt).tz(restaurantTz.value);
            eventData.date = inTz.format("YYYY-MM-DD");
            eventData.time = inTz.format("HH:mm:ss");
          }
        }

        // Ensure image has a default value if empty
        if (!eventData.image) {
          eventData.image =
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200";
        }

        if (editingEvent.value) {
          // Update existing event
          const updatedEvent = await api.updateEvent(
            editingEvent.value.id,
            eventData,
          );
          const index = events.value.findIndex(
            (e) => e.id === editingEvent.value!.id,
          );
          if (index !== -1) {
            events.value[index] = updatedEvent as Event;
          }
        } else {
          // Create new event
          const newEvent = await api.createEvent(eventData);
          events.value.push(newEvent as Event);
        }
        showForm.value = false;
        editingEvent.value = null;
      } catch (err) {
        console.error("Failed to save event:", err);
        saveError.value =
          err instanceof Error
            ? err.message
            : "Failed to save event. Please try again.";
      }
    };

    const handleDeleteEvent = (eventId: string) => {
      deleteConfirmId.value = eventId;
    };

    const confirmDelete = async () => {
      if (deleteConfirmId.value) {
        try {
          deleteError.value = "";
          await api.deleteEvent(deleteConfirmId.value);
          events.value = events.value.filter(
            (e) => e.id !== deleteConfirmId.value,
          );
          deleteConfirmId.value = null;
        } catch (err) {
          console.error("Failed to delete event:", err);
          deleteError.value =
            err instanceof Error
              ? err.message
              : "Failed to delete event. Please try again.";
        }
      }
    };

    onMounted(() => {
      setTitle(t("events.management.title"));
      loadEvents();
    });

    return () => (
      <div class="flex flex-1 flex-col">
        {/* Header */}
        <AppSubpageHeader
          icon="event"
          title={t("events.management.title")}
          description={t("events.management.description")}
        >
          {{
            meta: () => (
              <>
                <span class="material-symbols-outlined text-sm">
                  calendar_month
                </span>
                {events.value.length} {t("events.management.events_suffix")}
              </>
            ),
            actions: () => (
              <button class="btn btn-primary btn-sm" onClick={handleAddEvent}>
                <span class="material-symbols-outlined">add</span>
                {t("events.management.create_event")}
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          {/* Content */}
          <AppSubpageContent>
            {error.value ? (
              <ErrorDisplayer
                error={error.value}
                title={t("events.management.error_title")}
                onRetry={loadEvents}
              />
            ) : !loading.value && events.value.length === 0 ? (
              <div class="flex items-center justify-center h-full">
                <div class="text-center">
                  <div class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center mx-auto mb-4">
                    <span class="material-symbols-outlined text-base-content/30 text-3xl">
                      event
                    </span>
                  </div>
                  <h3 class="font-medium text-accent mb-1">
                    {t("events.management.empty_title")}
                  </h3>
                  <p class="text-sm text-base-content/60 mb-4">
                    {t("events.management.empty_description")}
                  </p>
                  <button
                    class="btn btn-primary btn-sm"
                    onClick={handleAddEvent}
                  >
                    <span class="material-symbols-outlined">add</span>
                    {t("events.management.create_event")}
                  </button>
                </div>
              </div>
            ) : (
              <ResponsiveTable
                columns={columns}
                rows={sortedEvents.value}
                rowKey={(row: Event) => row.id}
                onRowClick={handleEditEvent}
                loading={loading.value}
                emptyMessage={t("events.management.empty_title")}
              />
            )}
          </AppSubpageContent>

          {/* Event Form Modal */}
          {showForm.value && (
            <EventForm
              event={editingEvent.value}
              isOpen={showForm.value}
              onClose={() => {
                showForm.value = false;
                editingEvent.value = null;
                saveError.value = "";
              }}
              onSave={handleSaveEvent}
            />
          )}

          {/* Save Error Toast */}
          {saveError.value && (
            <div class="toast toast-top toast-center z-50">
              <div class="alert alert-error">
                <span class="material-symbols-outlined">error</span>
                <span>{saveError.value}</span>
                <button
                  class="btn btn-sm btn-ghost"
                  onClick={() => (saveError.value = "")}
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmId.value && (
            <div class="modal modal-open">
              <div class="modal-box">
                <h3 class="font-bold text-lg text-accent mb-4">
                  {t("events.management.confirm_delete")}
                </h3>
                <p class=" mb-6">
                  {t("events.management.confirm_delete_message")}
                </p>
                {deleteError.value && (
                  <div class="alert alert-error mb-4">
                    <span class="material-symbols-outlined">error</span>
                    <span>{deleteError.value}</span>
                  </div>
                )}
                <div class="flex gap-3 justify-end">
                  <button
                    class="btn btn-ghost"
                    onClick={() => {
                      deleteConfirmId.value = null;
                      deleteError.value = "";
                    }}
                  >
                    {t("events.management.cancel")}
                  </button>
                  <button class="btn btn-error" onClick={confirmDelete}>
                    <span class="material-symbols-outlined">delete</span>
                    {t("events.management.delete_event")}
                  </button>
                </div>
              </div>
              <div
                class="modal-backdrop"
                onClick={() => {
                  deleteConfirmId.value = null;
                  deleteError.value = "";
                }}
              />
            </div>
          )}
        </AppSubpage>
      </div>
    );
  },
});
